import React, { useEffect } from 'react'
import { Input, Typography } from 'antd'
import { Coding, QuestionnaireItem } from '@medplum/fhirtypes'
import { QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL } from 'fhir/Questionnaire/constants'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { extractDataFromReference } from './helpers'
import { HalfWidthContainer, ItemWithTextDetailsContainer } from './styles'
import {
  useValidateAndUpdateValues,
  ValidateFormValuesType
} from '../PatAssessment/hooks/useValidateAndUpdateFormValues'
import { generateScoreAndDurationString } from '../PatAssessment/helpers'
import { UNANSWERED_DECIMAL_DEFAULT } from '../PatAssessment/constants'
import { useGenerateAnswerFromSectionResults } from '../PatAssessment/hooks/useGenerateAnswerFromSectionResults'

const { Text } = Typography

export interface SummaryInputProps {
  question: QuestionnaireItem
  data: Record<string, unknown>
  dataReference: Coding
}

// This component is used to display a disabled input field due to data being retrieved
// from an external source or from the results of a section.

export const SummaryInput: React.FC<SummaryInputProps> = ({ question, data, dataReference }) => {
  const {
    answers,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()

  const validateAndUpdateFormValues = useValidateAndUpdateValues()
  let questionLinkId
  let questionText
  if (typeof question.linkId === 'string') {
    questionLinkId = question.linkId
  }
  if (typeof question.text === 'string') {
    questionText = question.text
  }

  const sectionName = questionLinkId.split('/')[1]

  const { generatedItemValue } = useGenerateAnswerFromSectionResults({
    questionType: question.type as QuestionnaireAnswerType,
    questionLinkId: questionLinkId,
    questionText: questionText
  })
  // Determines if we are grabbing data from patientChart
  const isItemDataExternal = data && dataReference

  let initialValue = question.initial?.[0]
  if (isItemDataExternal) {
    const extractedValue = extractDataFromReference(data, dataReference)
    if (extractedValue) {
      initialValue = extractedValue
    }
  }

  const generateDisplayValue = (): string => {
    let answerValue
    let displayValue
    switch (question.type) {
      case QuestionnaireAnswerType.DECIMAL:
        answerValue =
          answers[questionLinkId]?.valueDecimal ??
          initialValue?.valueDecimal ??
          UNANSWERED_DECIMAL_DEFAULT
        displayValue = generateScoreAndDurationString(answerValue, sectionName)
        break
      case QuestionnaireAnswerType.STRING:
        displayValue = answers[questionLinkId]?.valueString ?? initialValue?.valueString ?? 'N/A'
        break
    }
    return displayValue
  }

  const displayValue = generateDisplayValue()

  useEffect(() => {
    if (isItemDataExternal) {
      validateAndUpdateFormValues({
        type: question.type as QuestionnaireAnswerType,
        linkId: questionLinkId,
        text: questionText,
        value: initialValue ?? ''
      })
    } else if (generatedItemValue) {
      validateAndUpdateFormValues(generatedItemValue as ValidateFormValuesType)
    }
  }, [
    generatedItemValue,
    isItemDataExternal,
    initialValue,
    questionText,
    questionLinkId,
    question.type
  ])

  const renderQuestions = (): JSX.Element => {
    return (
      <ItemWithTextDetailsContainer
        key={questionLinkId}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        onClick={() => questionLinkId && createCommentClickHandler(questionLinkId)}
        id={getCssSelectorForLinkId(questionLinkId)}
      >
        <HalfWidthContainer>
          <Text strong style={{ fontSize: '16px' }}>
            {question?.text}
          </Text>
          {question?.extension?.map((extItem) => {
            if (extItem?.url === QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL) {
              return extItem?.valueCodeableConcept?.coding?.map((code) => {
                return (
                  <Text key={code?.display} type="secondary" style={{ display: 'block' }}>
                    {code?.display}
                  </Text>
                )
              })
            }
          })}
        </HalfWidthContainer>
        <HalfWidthContainer>
          <Input value={displayValue} disabled />
        </HalfWidthContainer>
      </ItemWithTextDetailsContainer>
    )
  }

  return renderQuestions()
}
